import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';

import { useServiceError } from '@/hooks';
import {
  createSendOption,
  detailSendOption,
  editSendOption,
  listSendOption,
  removeSendOption,
  sendOptionCommunication,
  sendOptionCommunicationWithBilling,
  sendOptionCommunicationWithVehicle,
  sendOptionFutureServicesCommunication,
  sendOptionNextReviewsCommunication,
} from './SendOptions.service';
import {
  CreateSendOptionData,
  EditSendOptionData,
  ListSendOptionParams,
} from './SendOptions.types';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';

export function useListSendOptions(params?: ListSendOptionParams) {
  const query = useInfiniteQuery(
    ['sendOptions', { ...params }],
    ({ pageParam }) => {
      const req: ListSendOptionParams = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listSendOption(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useCreateSendOption() {
  const mutation = useMutation(
    (data: CreateSendOptionData) => createSendOption(data),
    {
      onSuccess() {
        notification.success({
          message: 'Opção de envio cadastrado com sucesso!',
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useEditSendOption() {
  const mutation = useMutation(
    ({ optionId, data }: { optionId: number; data: EditSendOptionData }) =>
      editSendOption(optionId, data),
    {
      onSuccess() {
        notification.success({
          message: 'Opção de envio atualizado com sucesso!',
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDetailSendOption() {
  const mutation = useMutation(detailSendOption);

  useServiceError(mutation.error);

  return mutation;
}

export function useRemoveSendOption() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation((id: number) => removeSendOption(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'shippingOptions.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'shippingOptions.remove.notification.successDescription',
        }),
      });

      queryClient.invalidateQueries(['sendOptions']);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Cannot Delete default Send Option.')
              ? 'Não é possível excluir uma opção de envio padrão.'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useCrmSendOptionCommunication() {
  return useMutation(sendOptionCommunication, {
    onSuccess() {
      notification.success({
        message: 'E-mails enviados!',
        description:
          'Foi criado uma fila de envio para os emails selecionados!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useCrmSendOptionCommunicationWithVehicle() {
  return useMutation(sendOptionCommunicationWithVehicle, {
    onSuccess() {
      notification.success({
        message: 'E-mails enviados!',
        description:
          'Foi criado uma fila de envio para os emails selecionados!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useCrmSendOptionCommunicationWithBilling() {
  return useMutation(sendOptionCommunicationWithBilling, {
    onSuccess() {
      notification.success({
        message: 'E-mails enviados!',
        description:
          'Foi criado uma fila de envio para os emails selecionados!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useCrmSendOptionFutureServiceCommunication() {
  return useMutation(sendOptionFutureServicesCommunication, {
    onSuccess() {
      notification.success({
        message: 'E-mails enviados!',
        description:
          'Foi criado uma fila de envio para os emails selecionados!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useCrmSendOptionNextReviewCommunication() {
  return useMutation(sendOptionNextReviewsCommunication, {
    onSuccess() {
      notification.success({
        message: 'E-mails enviados!',
        description:
          'Foi criado uma fila de envio para os emails selecionados!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}
